<template>
  <section class="contact-form-section" role="form">
    <form
      :action="formAction"
      id="contact-form"
      method="POST"
      class="contact-form-section"
      @submit="submit"
    >
      <label :class="{ missing: showValidation && validation.region }"
        >{{ regionLabel }}
        <select
          v-model="form.region"
          ref="region"
          name="00N2E00000IMMp3"
          :class="{ placeholder: !form.region }"
          required
          v-on:invalid="onInvalid($event, 'region')"
        >
          <option value="" disabled>{{ regionPlaceholder }}</option>
          <option v-for="region in regions" :value="region.label" :key="region.label">{{
            region.label
          }}</option>
        </select>
      </label>

      <!-- Hidden SF office field, received from param -->
      <input
        v-show="false"
        maxlength="100"
        name="00N2E00000JkuPL"
        :value="form.office"
        type="text"
      />

      <!-- Hidden SF service field, received from param -->
      <input
        v-show="false"
        maxlength="100"
        name="00N2E00000Hupon"
        :value="form.serviceName"
        type="text"
      />

      <div class="form-item">
        <label :class="{ missing: showValidation && validation.services }">{{ areaLabel }} </label>
        <v-select
          v-model="form.services"
          ref="services"
          :reduce="(service) => service.title"
          :options="services"
          label="title"
          :placeholder="areaPlaceholder"
          autocomplete="new-password"
          multiple
        >
          <!-- Overriding the search box in order to preserve the placeholder. -->
          <!-- https://vue-select.org/api/slots.html#search -->
          <template #search="{ attributes, events }">
            <input
              class="vs__search"
              v-bind="attributes"
              v-on="events"
              autocomplete="new-password"
              aria-autocomplete="list"
              :placeholder="areaPlaceholder"
              :required="form.services.length === 0"
              v-on:invalid="onInvalid($event, 'services')"
            />
          </template>
        </v-select>

        <!-- Hidden SF input -->
        <select
          v-show="false"
          v-model="form.services"
          multiple="multiple"
          :name="salesforceLeadServicesName"
        >
          <option v-for="(service, index) in services" :value="service.title" :key="'s_' + index">{{
            service.title
          }}</option>
        </select>
      </div>

      <label :class="{ missing: showValidation && validation.enquiry }">
        {{ enquiryLabel }}
        <textarea
          v-model="form.enquiry"
          :placeholder="form.enquiryPlaceholder"
          name="description"
          required
          ref="enquiry"
          :style="{ height: enquiryHeight }"
          v-on:invalid="onInvalid"
        ></textarea>
      </label>
      <label :class="{ missing: showValidation && validation.first_name }">
        {{ firstNameLabel }}
        <input
          type="text"
          v-model.trim="form.first_name"
          :placeholder="firstNamePlaceholder"
          name="first_name"
          required
          v-on:invalid="onInvalid"
        />
      </label>
      <label :class="{ missing: showValidation && validation.last_name }">
        {{ lastNameLabel }}
        <input
          type="text"
          v-model.trim="form.last_name"
          :placeholder="lastNamePlaceholder"
          name="last_name"
          required
          v-on:invalid="onInvalid"
        />
      </label>
      <label :class="{ missing: showValidation && validation.email }">
        {{ emailLabel }}
        <input
          type="email"
          v-model.trim="form.email"
          :placeholder="emailPlaceholder"
          name="email"
          required
          v-on:invalid="onInvalid"
        />
      </label>
      <div class="form-item">
        <label :class="{ missing: showValidation && validation.phoneNumber }">
          {{ phoneNumberLabel }}
        </label>
        <div class="phone">
          <select required ref="countryCode" v-model="form.countryCode">
            <option v-for="(code, country) in COUNTRY_CODES" :value="country" :key="code + country"
              >{{ country }}: +{{ code }}
            </option>
          </select>
          <input
            type="tel"
            v-model.trim="form.phoneNumber"
            :placeholder="phoneNumberPlaceholder"
            required
            v-on:invalid="onInvalid"
          />
        </div>
      </div>
      <label :class="{ missing: showValidation && validation.company }">
        {{ companyLabel }}
        <input
          type="text"
          v-model="form.company"
          :placeholder="companyPlaceholder"
          name="company"
          required
          v-on:invalid="onInvalid"
        />
      </label>

      <label>
        {{ jobTitleLabel }}
        <input
          type="text"
          v-model="form.jobTitle"
          :placeholder="jobTitlePlaceholder"
          name="title"
          maxlength="40"
          required
          v-on:invalid="onInvalid"
        />
      </label>

      <label v-if="industries && industries.length">
        {{ industryLabel }}
        <select required ref="industry" v-model="form.industry" name="industry">
          <option value="" disabled>{{ industryPlaceholder }}</option>
          <option v-for="industry in industries" :value="industry.value" :key="industry.value"
            >{{ industry.value }}
          </option>
        </select>
      </label>

      <p class="disclaimer" v-html="disclaimer"></p>
      <input type="hidden" name="oid" :value="salesforceOid" />
      <input type="hidden" name="retURL" v-model="returnUrl" />
      <input type="hidden" name="phone" v-model="phone" />
      <input type="hidden" name="country" v-model="form.geoCountryName" />
      <input type="hidden" name="lead_source" value="Website" />
      <input type="hidden" name="00NHu00000KKaEJ" v-model="returnReferrerUrl" />
      <!-- Lead source name -->
      <input type="hidden" :name="salesforceLeadSourceName" value="vml.com" />
      <input
        type="hidden"
        :name="salesforceLeadCampaignSource"
        v-model="campaignData.campaign_source"
      />
      <input
        type="hidden"
        :name="salesforceLeadCampaignMedium"
        v-model="campaignData.campaign_medium"
      />
      <input
        type="hidden"
        :name="salesforceLeadCampaignName"
        v-model="campaignData.campaign_name"
      />
      <div v-cloak v-if="isFromJapan">
        <p class="disclaimer" v-if="!isJaPath">
          Please read our Japan Personal Information Consent Statement
          <a href="/privacy-policy-japan" class="text-bold" target="_blank">here</a>.<br />
          By clicking ‘Submit’ you are agreeing to our Personal Information Consent Statement in
          Japan.
        </p>
        <p class="disclaimer">
          日本の個人情報取扱い同意書を確認ください。<a
            href="/ja/privacy-policy-japan"
            class="text-bold"
            target="_blank"
            >here</a
          >.<br />
          送信をクリックすると、日本の個人情報取扱い同意書に同意したことになります。
        </p>
      </div>
      <button type="submit" class="blue text-bold" :disabled="loading">
        <slot></slot>
      </button>
      <div v-if="hasErrors" class="complete-fields">{{ error }}</div>
      <p>* {{ validationCustomMessage }}</p>
    </form>
  </section>
</template>
<script>
import geolocation from '../util/user_location';
import vSelect from 'vue-select';
import analytics from '../util/analytics.js';
import Helpers from '../util/helpers.js';
import { COUNTRY_CODES } from '../util/constants';

export default {
  components: { vSelect },

  props: {
    services: Array,
    regions: Array,
    industries: Array,
    formAction: String,
    salesforceOid: String,
    salesforceLeadSourceName: String,
    salesforceLeadCampaignSource: String,
    salesforceLeadCampaignMedium: String,
    salesforceLeadCampaignName: String,
    salesforceLeadServicesName: String,
    regionLabel: String,
    regionPlaceholder: String,
    areaLabel: String,
    areaPlaceholder: String,
    enquiryLabel: String,
    enquiryPlaceholder: String,
    firstNameLabel: String,
    firstNamePlaceholder: String,
    lastNameLabel: String,
    lastNamePlaceholder: String,
    emailLabel: String,
    emailPlaceholder: String,
    phoneNumberLabel: String,
    phoneNumberPlaceholder: String,
    companyLabel: String,
    companyPlaceholder: String,
    jobTitleLabel: String,
    jobTitlePlaceholder: String,
    industryLabel: String,
    industryPlaceholder: String,
    disclaimer: String,
    thankYou: String,
    error: String,
    validationCustomMessage: String
  },

  data: function() {
    let params = Helpers.deparam(window.location.search),
      services = [],
      office,
      region;

    if (params.service) {
      let serviceParams = Array.isArray(params.service) ? params.service : [params.service];
      services = serviceParams
        .map((id) => {
          return this.services.find((service) => {
            let normalizedId = typeof id === 'string' ? id.toLowerCase() : id;

            // Match param as fictional serivce id like "other" or "newBusiness"
            if (typeof service.id === 'string' && service.id.toLowerCase() === normalizedId) {
              return true;
            }

            // Match param as service numerical id
            if (typeof service.id === 'number' && service.id == normalizedId) {
              return true;
            }

            // Match param as service title
            if (service.title && service.title.toLowerCase() == normalizedId) {
              return true;
            }
          });
        })
        .filter(Boolean)
        .map((s) => s.title);
    }

    if (params.office) {
      office = Array.isArray(params.office) ? params.office[0] : params.office;
    }

    if (params.region) {
      // Some URLs pass us a region ID, convert to label if that's the case.
      region = this.regions.find((r) => r.value.toLowerCase() === params.region.toLowerCase());
      if (region) {
        region = region.label;
      } else {
        region = params.region;
      }
    }

    return {
      COUNTRY_CODES: COUNTRY_CODES,
      loading: false,
      form: {
        services: services,
        serviceName: services.length ? services[0] : null,
        office: office,
        region: region ? region : '',
        enquiry: '',
        first_name: '',
        last_name: '',
        email: '',
        countryCode: COUNTRY_CODES['US'],
        phoneNumber: '',
        geoCountryName: '',
        company: '',
        jobTitle: '',
        industry: ''
      },
      showValidation: false,
      enquiryHeight: '',
      campaignData: analytics.getCampaignData() || {}
    };
  },

  mounted() {
    // Ensure US label is being shown by default.
    this.setCountryCode('US');

    geolocation.location.then((userLocation) => {
      if (userLocation.data && userLocation.data.countryCode) {
        this.form.geoCountryName = userLocation.data.countryName;
        if (COUNTRY_CODES[userLocation.data.countryCode]) {
          this.setCountryCode(userLocation.data.countryCode);
        }
      }
    });

    // Make ure that the thank you not is in view https://jira.uhub.biz/browse/JWTSOLWTCOM-734
    if (this.$refs.note) {
      this.$refs.note.scrollIntoView();
    }
  },

  computed: {
    phone() {
      let phoneNumber = this.form.phoneNumber.trim().replace(/^\+/, ''),
        countryCode = COUNTRY_CODES[this.form.countryCode];

      return '+' + countryCode + ' ' + phoneNumber;
    },

    returnUrl() {
      return window.location.href.replace(/\?.+/, '') + '?success=1';
    },

    returnReferrerUrl() {
      return window.document.referrer;
    },

    hasErrors() {
      return this.showErrors && Object.values(this.validation).filter(Boolean).length > 0;
    },

    isFromJapan() {
      return this.form.geoCountryName === 'Japan';
    },

    isJaPath() {
      return /^\/ja\//i.test(window.location.pathname);
    },

    validation() {
      return {
        region: !this.form.region,
        services: !this.form.services || !this.form.services.length,
        enquiry: !this.form.enquiry,
        first_name: !this.form.first_name,
        last_name: !this.form.last_name,
        email: !this.form.email,
        phoneNumber: !this.form.phoneNumber,
        company: !this.form.company,
        jobTitle: !this.form.jobTitle
      };
    }
  },

  watch: {
    'form.enquiry'() {
      // https://github.com/devstark-com/vue-textarea-autosize
      this.enquiryHeight = 'auto';
      this.$nextTick(() => {
        if (this.$refs.enquiry) {
          let contentHeight = this.$refs.enquiry.scrollHeight + 1;
          this.enquiryHeight = contentHeight + 'px';
        }
      });
    }
  },

  methods: {
    submit: function(event) {
      if (Object.values(this.validation).filter(Boolean).length) {
        event.preventDefault();
        this.showValidation = true;
      } else {
        let label = `Region: ${this.form.region} | Interest: ${this.form.services.join(', ')}`;
        analytics.trackEvent('Contact Us', { action: 'form submission', label: label });
        this.loading = true;
        this.showValidation = false;
      }
    },

    /**
     * Our country code selector can have duplicate values, so we need force specific options
     * so the label for the correct country is visible to the user. Without this, all
     * US users will see CA as the country because they share the same country code and CA is first
     * in the dropdown.
     * @param {String} countryCode The country code.
     */
    setCountryCode: function(countryCode) {
      if (!countryCode || !COUNTRY_CODES[countryCode]) {
        countryCode = 'US';
      }

      this.form.countryCode = countryCode;
    },

    /**
     * On invalid event for custom validation
     * @param event {Event} The event.
     * @param key {String} The validation object key.
     */
    onInvalid: function(event, key) {
      let v = event.currentTarget.validity;
      // Because we are overwriting browser validation we have to check any possible invalid type.
      // We cannot use invalid because we are setting the custom message.
      // https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
      let invalid =
        v.badInput ||
        v.patternMismatch ||
        v.rangeOverflow ||
        v.rangeUnderflow ||
        v.stepMismatch ||
        v.tooLong ||
        v.tooShort ||
        v.typeMismatch ||
        v.valueMissing;
      if ((!key && invalid) || (key && this.validation[key])) {
        event.currentTarget.setCustomValidity(this.validationCustomMessage);
      } else {
        event.currentTarget.setCustomValidity('');
        // We need to prevent default when clearing validation message,
        // vue select has an empty field that is required.
        // Note invalid event only triggers on required fields.
        event.preventDefault();
      }
    }
  }
};
</script>
